import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useRefreshUserAnalytics, useUserAnalyticsRefreshStatus, useUsers } from '@/hooks/use-admin'
import { useOrganizations } from '@/hooks/use-admin'
import { BarChart3, ChevronLeft, ChevronRight, ExternalLink, RefreshCw, Search } from 'lucide-react'
import type React from 'react'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export function AdminAnalyticsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '')
  const [refreshJobId, setRefreshJobId] = useState<string | null>(null)

  const page = Number(searchParams.get('page')) || 1
  const search = searchParams.get('search') || ''
  const organizationId = searchParams.get('organization_id')
    ? Number(searchParams.get('organization_id'))
    : undefined

  const { data, isLoading } = useUsers({
    page,
    limit: 20,
    ...(search && { search }),
    ...(organizationId && { organization_id: organizationId }),
  })
  const { data: orgsData } = useOrganizations({ limit: 100 })
  const refreshAnalytics = useRefreshUserAnalytics()
  const { data: refreshStatus } = useUserAnalyticsRefreshStatus(refreshJobId)
  const orgMap = new Map(orgsData?.organizations.map((org) => [org.id, org.name]) ?? [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const params = new URLSearchParams(searchParams)
    if (searchInput) {
      params.set('search', searchInput)
    } else {
      params.delete('search')
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  const handleRefreshAnalytics = async () => {
    const response = await refreshAnalytics.mutateAsync({
      scope: 'page',
      page,
      limit: 20,
      ...(search && { search }),
      ...(organizationId && { organization_id: organizationId }),
    })
    if (response.job_id) {
      setRefreshJobId(response.job_id)
    }
  }

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(newPage))
    setSearchParams(params)
  }

  const handleOrganizationFilter = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value === 'all') {
      params.delete('organization_id')
    } else {
      params.set('organization_id', value)
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground">View detailed platform analytics</p>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle>User Analytics</CardTitle>
                <CardDescription>
                  Cached per-user metrics. Refresh runs in the background.
                </CardDescription>
                {refreshStatus && (
                  <CardDescription>
                    Refresh status: {refreshStatus.status} ({refreshStatus.processed_count}/
                    {refreshStatus.target_count})
                  </CardDescription>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={organizationId?.toString() ?? 'all'}
                  onValueChange={handleOrganizationFilter}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by org" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {orgsData?.organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id.toString()}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder="Search users..."
                      className="pl-8 w-64"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                  <Button type="submit" variant="secondary">
                    Search
                  </Button>
                </form>
                <Button
                  variant="outline"
                  onClick={handleRefreshAnalytics}
                  disabled={refreshAnalytics.isPending || refreshStatus?.status === 'running'}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${
                      refreshAnalytics.isPending || refreshStatus?.status === 'running'
                        ? 'animate-spin'
                        : ''
                    }`}
                  />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="rounded-md border">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="h-10 px-4 text-left text-sm font-medium">Name</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Email</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Organization</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Messages</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Resumes</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">
                        Total Jobs Matched
                      </th>
                      <th className="h-10 px-4 text-left text-sm font-medium">
                        Learning Modules Generated
                      </th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Invited At</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Last Signed In</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Last Refreshed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.users.map((user) => (
                      <tr key={user.id} className="border-b">
                        <td className="p-4 text-sm">
                          {user.full_name ||
                            user.first_name ||
                            user.email ||
                            user.phone_number ||
                            '-'}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{user.email || '-'}</td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {user.organization_id ? orgMap.get(user.organization_id) || '-' : '-'}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {user.analytics_snapshot?.total_messages ?? '-'}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {user.analytics_snapshot?.total_resumes_uploaded ?? '-'}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {user.analytics_snapshot?.total_job_matches_ran ?? '-'}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {user.analytics_snapshot?.total_learning_skills ?? '-'}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {user.invited_at ? new Date(user.invited_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {user.analytics_snapshot?.last_signed_in
                            ? new Date(user.analytics_snapshot.last_signed_in).toLocaleDateString()
                            : '-'}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {user.analytics_snapshot?.computed_at
                            ? new Date(user.analytics_snapshot.computed_at).toLocaleString()
                            : '-'}
                        </td>
                      </tr>
                    ))}
                    {data?.users.length === 0 && (
                      <tr>
                        <td colSpan={10} className="p-8 text-center text-muted-foreground">
                          No users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {data && data.pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Page {data.page} of {data.pages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= data.pages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Google Analytics Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Google Analytics
            </CardTitle>
            <CardDescription>View detailed user behavior and traffic analytics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="aspect-video bg-gray-100 rounded-lg border-2 border-dashed flex items-center justify-center">
              <div className="text-center">
                <BarChart3 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Google Analytics Dashboard</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-md">
                  To embed Google Analytics reports, configure a Looker Studio dashboard or use the
                  GA4 Embed API.
                </p>
                <a
                  href="https://analytics.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:underline"
                >
                  Open Google Analytics
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Real-Time</CardTitle>
              <CardDescription>Current active users</CardDescription>
            </CardHeader>
            <CardContent>
              <a
                href="https://analytics.google.com/analytics/web/#/realtime"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                View Real-Time Report
                <ExternalLink className="h-3 w-3" />
              </a>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Acquisition</CardTitle>
              <CardDescription>Traffic sources and channels</CardDescription>
            </CardHeader>
            <CardContent>
              <a
                href="https://analytics.google.com/analytics/web/#/acquisition"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                View Acquisition Report
                <ExternalLink className="h-3 w-3" />
              </a>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Engagement</CardTitle>
              <CardDescription>User interactions and events</CardDescription>
            </CardHeader>
            <CardContent>
              <a
                href="https://analytics.google.com/analytics/web/#/engagement"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                View Engagement Report
                <ExternalLink className="h-3 w-3" />
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
